import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  DynamicDropdown,
  FullScreenLoading,
  StaticMultiSelectDropdown,
  TextInput,
} from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { IAddOrEditSahraUser } from "src/Interfaces/SahraTableInterfaces";
import {
  addSahraUser,
  editSahraUser,
  getAvailableSahraUserGroups,
  getSahraUserInfo,
} from "src/Requests/Sahra/sahraUsersRequests";
import { getUsers } from "src/Requests/connections";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface Props {
  userId?: number;
  onClose: () => void;
}

const AGGREGATE_GROUP_NAME = "تجمیعی";

const emptyFormValues: IAddOrEditSahraUser = {
  name: "",
  username: "",
  password: "",
  link: "",
  connectedSahraUserId: "",
  sahraUserGroupIds: [],
};

const AddOrEditSahraUser: React.FC<Props> = ({ onClose, userId }) => {
  const [formValues, setFormValues] =
    useState<IAddOrEditSahraUser>(emptyFormValues);
  const [sahraUserGroups, setSahraUserGroups] = useState<DropdownItem[]>([]);
  const [loading, setLoading] = useState(!!userId);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!userId;

  useEffect(() => {
    if (!userId) return;

    getSahraUserInfo(userId)
      .then((result) =>
        setFormValues({
          ...emptyFormValues,
          ...result,
          sahraUserGroupIds: result.sahraUserGroupIds ?? [],
        }),
      )
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    getAvailableSahraUserGroups()
      .then(setSahraUserGroups)
      .catch((error) => {
        setSahraUserGroups([]);
        packetErrorHandler(error);
      })
      .finally(() => setGroupsLoading(false));
  }, []);

  const aggregateGroupId = useMemo(() => {
    const aggregateGroup = sahraUserGroups.find(
      (group) => group.name.trim() === AGGREGATE_GROUP_NAME,
    );
    if (!aggregateGroup) return null;

    const id = Number(aggregateGroup.id);
    return Number.isFinite(id) ? id : null;
  }, [sahraUserGroups]);

  const hasAggregateGroup =
    aggregateGroupId !== null &&
    formValues.sahraUserGroupIds.some((id) => id === aggregateGroupId);

  useEffect(() => {
    if (aggregateGroupId === null || hasAggregateGroup) return;

    setFormValues((current) => ({
      ...current,
      sahraUserGroupIds: [
        ...(current.sahraUserGroupIds ?? []),
        aggregateGroupId,
      ],
    }));
  }, [aggregateGroupId, hasAggregateGroup]);

  const changeHandler = (name: string, value: any) => {
    const normalizedValue =
      name === "sahraUserGroupIds"
        ? (value as Array<number | string>).map(Number).filter(Number.isFinite)
        : value;

    setFormValues((current) => ({
      ...current,
      [name]: normalizedValue,
    }));
  };

  const checkDisabled = () => {
    if (
      groupsLoading ||
      aggregateGroupId === null ||
      !formValues.link ||
      !formValues.name ||
      !formValues.username
    )
      return true;

    return false;
  };

  const submitHandler = () => {
    if (checkDisabled()) return;

    setSaving(true);
    const request = isEdit
      ? editSahraUser(formValues)
      : addSahraUser(formValues);

    request
      .then((response) => {
        if (!response.data) {
          toast.error(response.msg);
          return;
        }

        toast.success(response.msg);
        onClose();
      })
      .catch(packetErrorHandler)
      .finally(() => setSaving(false));
  };

  if (loading || groupsLoading) return <FullScreenLoading />;

  return (
    <div className="w-full m-auto">
      <div className="whitespace-nowrap max-w-min m-auto mb-5">
        {isEdit ? "ویرایش" : "اضافه کردن"} اکانت
      </div>
      <div className="flex flex-col justify-start items-start gap-3 w-full m-auto border-2 border-blue-200 p-3">
        <TextInput
          label="نام"
          name="name"
          value={formValues.name}
          onChange={changeHandler}
        />
        <TextInput
          label="نام کاربری"
          name="username"
          value={formValues.username}
          onChange={changeHandler}
        />
        <TextInput
          label="رمز عبور"
          name="password"
          value={formValues.password}
          onChange={changeHandler}
          type="password"
          autoComplete="new-password"
        />
        <TextInput
          label="لینک"
          name="link"
          value={formValues.link}
          onChange={changeHandler}
        />
        <DynamicDropdown
          dropdown={{
            name: "connectedSahraUserId",
            placeholder: "اکانت متصل",
          }}
          value={formValues.connectedSahraUserId || null}
          getData={() => getUsers()}
          onChange={changeHandler}
        />
        <StaticMultiSelectDropdown
          name="sahraUserGroupIds"
          label="گروه‌های کاربران صحرا"
          items={sahraUserGroups}
          value={formValues.sahraUserGroupIds}
          onChange={changeHandler}
          withTagBox
          lockedValues={aggregateGroupId === null ? [] : [aggregateGroupId]}
          disabled={aggregateGroupId === null}
        />
        {aggregateGroupId === null ? (
          <div className="text-xs text-red-600">
            گروه «تجمیعی» یافت نشد؛ امکان ثبت کاربر وجود ندارد.
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-500">
              عضویت در گروه «تجمیعی» خودکار است و قابل حذف نیست.
            </div>
          </>
        )}
        <Button
          text="ثبت"
          onClick={submitHandler}
          variant="contained"
          disabled={checkDisabled()}
          loading={saving}
        />
      </div>
    </div>
  );
};

export default AddOrEditSahraUser;
